import axiosHttpClient from '#helpers/axiosHttpClient.js'
import handleApiError from '#helpers/logging/handleApiError.js'
import handleApiObject from '#helpers/logging/handleApiObject.js'
import Logger from '#helpers/logging/Logger.js'
import { url_user_preferences } from '#src/constants/endpoints.js'
import { defaultExpiresTime, errorRetryTime, nowTime } from '#stores/storeDefaults.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useColorMode, useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, shallowRef, watchEffect } from 'vue'

const defaults = {
	buttons: 'icon_text',
	colorMode: 'light',
	gravatar: '0',
	dateFormat: 'YYYY-MM-DD',
	dateSeparator: '-',
	timeFormat: '24',
	treeCollapse: 'none',
	layout: 'classic',
	manualsModeViewer: 'manuals',
	manualsPageBreaksManage: true,
	manualsPageBreaksViewer: false,
}

export const usePreferencesStore = defineStore('preferences', () => {
	const preferences = useStorage('preferences', defaults, sessionStorage, { mergeDefaults: true })
	const loading = shallowRef(false)
	const saving = shallowRef(false)
	const expires = useStorage('preferences-expires', null, sessionStorage)
	const { store: themeStore, state: resolvedColorMode } = useColorMode({ attribute: 'theme' })

	watchEffect(() => {
		themeStore.value = preferences.value.colorMode
	})

	const get = preference => {
		// fetchPreferences()
		// these are the fallback values for when this inevitably crashes in edge cases:
		// if (preference === 'dateFormat') { return language.value.dateFormat }
		// if (preference === 'dateSeparator') { return language.value.dateSeparator }
		return preferences.value?.[preference] ?? false
	}

	const list = computed(() => {
		// fetchPreferences()
		return preferences.value
	})

	/**
	 * Fetch all the preferences.
	 * @param {boolean} [force] - Whether to force fetching the preferences
	 * @returns {Promise} - A promise to act upon
	 */
	async function fetchPreferences(force = false) {
		if (force) {
			loading.value = false
		}
		if (loading.value) {
			return
		}
		if (new Date().getTime() < expires.value && !force) {
			return
		}
		const auth = useAuthorizationStore()
		if (!auth.accessToken) {
			return
		}

		loading.value = true
		await axiosHttpClient
			.get(url_user_preferences)
			.then(response => {
				const data = response?.data ?? {}
				preferences.value = { ...defaults, ...data }
				expires.value = nowTime() + defaultExpiresTime

				if (data.dateFormat && data.dateSeparator) {
					return
				}
				if (!data.dateFormat) {
					preferences.value.dateFormat ??= 'DD-MM-YYYY'
				}
				if (!data.dateSeparator) {
					preferences.value.dateSeparator ??= '-'
				}
			})
			.catch(e => {
				Logger.debug('usePreferencesStore: fetch failed')
				expires.value = nowTime() + errorRetryTime
				handleApiError(e)
			})
			.finally(() => {
				loading.value = false
			})
	}

	/**
	 * Save the preferences.
	 * @returns {Promise<void>}
	 */
	async function save() {
		saving.value = true
		await axiosHttpClient
			.put(url_user_preferences, preferences.value)
			.catch(handleApiObject)
			.finally(() => {
				saving.value = false
			})
	}

	/**
	 * Clean up the data.
	 */
	function remove() {
		loading.value = false
		preferences.value = defaults
		expires.value = 0
	}

	return {
		resolvedColorMode,
		preferences,
		loading,
		saving,
		expires,
		get,
		list,
		fetch: fetchPreferences,
		save,
		remove,
	}
})
